import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { format } from 'app/shared/utils/string.util';
import { IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { workdayEndpoints, workdayVersions } from 'app/shared/utils/connector-metadata-static-consts';
import { workdayAuthTypes } from 'shell-models-lib';

const basicAuthControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'userName',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            description: stringResources.Workday_Username_Description
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            disableAddParameter: true,
            child:
                {
                    type: 'password',
                    label: stringResources.ModelName_Password,
                    required: true,
                    isLabelVertical: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Workday_DisplayText),
            stringResources.ModelName_Password
        )
    }
];

const dataflowWorkdaySource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.Workday,
    displayName: stringResources.Workday_DisplayText,
    settings: adfModelTypeMap.workday.dataflowSettings,
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'textarea',
                bind: 'request',
                rows: 15,
                required: true,
                label: stringResources.Workday_Request_DisplayText,
                description: stringResources.Workday_Request_Description,
                isDataFlowExpression: true,
                supportMonaco: true,
                monacoLang: 'xml',
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        }
    ],
    useInlineProjection: true
};

const controls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'subDomain',
            label: stringResources.ModelName_Subdomain,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            description: stringResources.Workday_Subdomain_Description
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'tenantId',
            label: stringResources.ModelName_Tenant,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            description: stringResources.Workday_TenantId_Description
        }
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: workdayAuthTypes.BASIC
            }
        ],
        isLabelVertical: true,
        defaultValue: workdayAuthTypes.BASIC,
        cases: [
            {
                caseValue: workdayAuthTypes.BASIC,
                children: basicAuthControls
            }
        ]
    },
    {
        type: 'parameterizable',
        child: {
            type: 'select',
            bind: 'serviceVersion',
            label: stringResources.Workday_Service_Version_DisplayText,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            description: stringResources.Workday_Service_Version_Description,
            editable: true,
            placeholderText: stringResources.Workday_Service_Version_Placeholder,
            options: workdayVersions.reverse().map((version) => ({label: version, value: version})),
            defaultValue: workdayVersions[0]
        },
    },
    {
        type: 'select-switch',
        bind: 'service',
        label: stringResources.ModelName_Service,
        isLabelVertical: true,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        options: Object.keys(workdayEndpoints).map((service) => ({ label: service, value: service})),
        defaultValue: Object.keys(workdayEndpoints)[0],
        description: stringResources.Workday_Service_Description,
        cases: Object.keys(workdayEndpoints).map((service) => ({
            caseValue: service,
            children: [
                {
                    type: 'select',
                    bind: 'operation',
                    label: stringResources.ExtensibleActivity_OperationLabel,
                    isLabelVertical: true,
                    hasFilter: true,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    options: Object.values(workdayEndpoints)[Object.keys(workdayEndpoints).indexOf(service)].map((operation) => ({ label: operation, value: operation})),
                    defaultValue: Object.values(workdayEndpoints)[Object.keys(workdayEndpoints).indexOf(service)][0],
                    description: stringResources.Workday_Operation_Description
                },
            ]
        }))
    },
];

export const workdayMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.workday.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.Workday_DisplayText),
    linkedServiceKind: 'unknown',
    icon: {
        className: 'svg-i-100x100-workday'
    },
    category: ['ServicesApps'],
    onlyAzureIR: true,
    copySourceTypes: [],
    copySinkTypes: [],
    isFirstClass: true,
    supportDataFlowSource: true,
    dataflowSource: [dataflowWorkdaySource],
    supportFirstClassParameterExperience: true,
    controls
};
